import cx from 'classnames';
import { forwardRef } from 'react';

import type {
  ComponentColor,
  ComponentColorOrDefault,
  ComponentSize,
} from '../../utility';
import { useComponentColorClassName } from '../../utility';

import { BUTTON_SIZE_CLASSES } from './buttonSize';

const BUTTON_COLOR_CLASSES: Record<ComponentColor, string> = {
  Orange: 'peer-enabled:peer-checked:tw-text-orange',
  Red: 'peer-enabled:peer-checked:tw-text-red',
  Green: 'peer-enabled:peer-checked:tw-text-green',
  Blue: 'peer-enabled:peer-checked:tw-text-blue',
  Primary: '',
};

type SegmentedButtonVariant = 'Elevated' | 'Gray';

const BUTTON_VARIANT_CLASSES: Record<SegmentedButtonVariant, string> = {
  Elevated: 'peer-checked:tw-surface-elevated peer-checked:tw-shadow-30',
  Gray: 'peer-checked:tw-bg-fill-primary',
};

export interface SegmentedButtonProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  type?: 'radio' | 'checkbox';
  size?: ComponentSize;
  variant?: SegmentedButtonVariant;
  color?: ComponentColorOrDefault;
  children?: React.ReactNode;
  buttonClassName?: string;
}

/**
 * A radio or checkbox input which looks like a button
 */
export const SegmentedButton = forwardRef<
  HTMLInputElement,
  SegmentedButtonProps
>(
  (
    {
      className,
      buttonClassName,
      type = 'radio',
      size = 32,
      variant = 'Elevated',
      color,
      children,
      ...rest
    },
    ref,
  ) => {
    const colorClassName = useComponentColorClassName(
      color,
      BUTTON_COLOR_CLASSES,
    );

    return (
      // A <label> wrapping the input gives the input its accessible name from
      // this element's own text content — native behavior, so text (or
      // text + icon) children need no per-caller aria-label. An icon-only
      // caller still needs an explicit aria-label — an <svg> has no text
      // content for the label to pick up.
      <label className={cx(className, 'tw-inline-grid')}>
        <input
          type={type}
          className={cx(
            'tw-peer',
            'tw-opacity-0',
            'tw-col-start-1',
            'tw-row-start-1',
            'tw-w-full', // safari needs this to stretch the checkbox
            'tw-h-full', // safari needs this to stretch the checkbox
            'tw-cursor-pointer',
            'disabled:tw-cursor-not-allowed',
          )}
          {...rest}
          ref={ref}
        />
        {/* aria-hidden below drops the decorative button's text from the
            label's accessible-name computation, so this sr-only duplicate
            keeps every plain-text/icon-text caller named without requiring
            each one to pass an explicit aria-label. */}
        <span className="tw-sr-only">{children}</span>
        {/* Presentational only — aria-hidden removes it (and its `button`
            role) from the accessibility tree so it can't be picked up as a
            second, unlabeled `button` alongside the real ones on the page.
            Stays a real <button> (not a <span>) because several callers
            style it via a `[&>button]` child selector; it never received
            clicks or focus anyway (tw-pointer-events-none, tabIndex={-1}) —
            the input above handles both. */}
        <button
          type="button"
          aria-hidden="true"
          className={cx(
            'tw-col-start-1',
            'tw-row-start-1',
            buttonClassName,
            BUTTON_SIZE_CLASSES[size],
            colorClassName,
            BUTTON_VARIANT_CLASSES[variant],
            'peer-checked:tw-font-medium',
            'peer-enabled:peer-active:tw-brightness-90',
            'peer-enabled:peer-hover:tw-brightness-110',
            'peer-enabled:tw-text-label-secondary',
            'peer-disabled:tw-text-label-quaternary',
            'peer-focus-visible:tw-outline',
            'tw-pointer-events-none',
          )}
          tabIndex={-1}
        >
          {children}
        </button>
      </label>
    );
  },
);
